import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, ArrowRight, PackageOpen } from "lucide-react";
import api from "../lib/api";

export const ProductCard = ({ product, index = 0 }) => {
  const price = product.licenses?.creator?.price ?? product.price;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -6 }}
      data-testid={`product-card-${product.slug}`}
    >
      <Link
        to={`/products/${product.slug}`}
        className="group block bg-gradient-to-b from-white/5 to-white/[0.02] border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all duration-500 h-full"
      >
        <div className="relative h-52 overflow-hidden bg-purple-900/10">
          {product.badge && (
            <span className="absolute top-4 left-4 z-10 px-3 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full">
              {product.badge}
            </span>
          )}
          <img
            src={product.cover_image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-transparent to-transparent" />
        </div>
        <div className="p-6">
          <span className="text-xs font-medium text-purple-400">{product.category_name}</span>
          <h3 className="text-lg font-bold text-white mt-2 mb-2 group-hover:text-purple-300 transition-colors line-clamp-2">
            {product.name}
          </h3>
          <p className="text-sm text-gray-400 line-clamp-2 mb-4">{product.short_description}</p>
          <div className="flex items-center justify-between">
            <span className="text-white font-bold">
              {typeof price === "number" ? `From ₹${price}` : price}
            </span>
            <span className="text-purple-400 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
              View <ArrowRight className="w-4 h-4 ml-1" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (activeCat !== "all") params.category = activeCat;
    if (search) params.search = search;
    if (sort) params.sort = sort;
    const t = setTimeout(() => {
      api
        .get("/products", { params })
        .then((r) => setProducts(r.data))
        .catch(() => setProducts([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [activeCat, search, sort]);

  return (
    <div className="min-h-screen bg-[#050506] pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Explore OrvDigital</h1>
          <p className="text-lg text-gray-400 max-w-2xl">
            Premium resources for creators, entrepreneurs and digital builders.
          </p>
        </motion.div>

        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              data-testid="products-search"
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-purple-500/50"
          >
            <option value="newest" className="bg-[#0B0B0D]">Newest</option>
            <option value="name" className="bg-[#0B0B0D]">Name A-Z</option>
          </select>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-12">
          <button
            onClick={() => setActiveCat("all")}
            data-testid="filter-all"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeCat === "all" ? "bg-purple-600 text-white" : "bg-white/5 text-gray-300 hover:bg-white/10"
            }`}
          >
            All Products
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCat === c.id ? "bg-purple-600 text-white" : "bg-white/5 text-gray-300 hover:bg-white/10"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24">
            <PackageOpen className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No products found. New resources are coming soon.</p>
          </div>
        ) : products.length === 1 ? (
          <div className="max-w-sm mx-auto sm:mx-0">
            <ProductCard product={products[0]} index={0} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
